import { Component, inject, computed, input, booleanAttribute, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TradingModeService } from '@core/services/trading-mode.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-paper-trading-badge',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './paper-trading-badge.html',
  styleUrls: ['./paper-trading-badge.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PaperTradingBadgeComponent {
  private readonly tradingMode = inject(TradingModeService);

  /**
   * Compact variant: render only the colour-blind-safe glyph + mode text
   * (PAPER/LIVE/UNKNOWN) and drop the trailing "· {env}" label. Used for the
   * mobile navbar (`flex md:hidden`) where horizontal space is tight, so the
   * trading-mode cue survives below 768px without the wider env suffix. The
   * tri-state, colours, glyph and aria-label are unchanged — same source of
   * truth as the full badge.
   */
  readonly compact = input(false, { transform: booleanAttribute });

  readonly paperTradingEnabled = this.tradingMode.isPaperTrading;
  readonly lastChecked = this.tradingMode.lastChecked;
  readonly statusText = this.tradingMode.tradingModeLabel;

  /**
   * GAP-005 tri-state pass-throughs. The template switches on `mode` so the
   * `unknown` state (startup, or after a health outage) renders an explicit
   * neutral pill instead of being collapsed into the binary PAPER/LIVE
   * ternary. `isStale` adds an "unverified" treatment to a retained reading.
   */
  readonly mode = this.tradingMode.mode;
  readonly isModeKnown = this.tradingMode.isModeKnown;
  readonly isStale = this.tradingMode.isStale;

  /** Short status text mirroring the service ('PAPER' | 'LIVE' | 'UNKNOWN'). */
  readonly statusGlyphText = this.tradingMode.tradingModeText;

  /** Colour-blind-safe glyph keyed off the tri-state mode. */
  readonly modeGlyph = computed(() => {
    switch (this.mode()) {
      case 'paper':
        return '▲';
      case 'live':
        return '■';
      default:
        // Open circle = pending / unverified (matches bb-status--neutral usage).
        return '○';
    }
  });

  readonly statusClass = computed(() => {
    switch (this.mode()) {
      case 'paper':
        return 'paper-trading';
      case 'live':
        return 'live-trading';
      default:
        return 'unknown-trading';
    }
  });

  /** Short environment label rendered after the mode (e.g. "Sandbox-Lab", "Prod EU-1"). */
  readonly envLabel = computed(() => {
    const name = (environment as { envName?: string }).envName;
    if (name) {
      return name;
    }
    switch (this.mode()) {
      case 'paper':
        return 'Sandbox';
      case 'live':
        return 'Prod';
      default:
        return 'Unverified';
    }
  });
}
